import React, { useContext, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { createUser } from "../../api/user-api.js";
import { ToastContext } from "../../contexts/toast-context.jsx";
import { OkDialog } from "../ui/ok-dialog.jsx";

export default function Register() {
  const { t } = useTranslation(["auth", "validation", "common"]);
  const { showError } = useContext(ToastContext);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const handleCloseSuccessDialog = () => {
    navigate("/login");
  }

  const schema = yup.object({
    name: yup
      .string()
      .max(120, t("maxLengthValidation", { field: t("name"), max: 120 }))
      .required(t("requiredValidation", { field: t("name") })),
    email: yup
      .string()
      .emailValidatorJs(t("emailValidation", { field: t("email") }))
      .required(t("requiredValidation", { field: t("email") })),
    password: yup
      .string()
      .min(8, t("minLengthValidation", { field: t("password"), min: 7 }))
      .required(t("requiredValidation", { field: t("password") })),
    tnc: yup
      .boolean()
      .oneOf([true], t("requiredValidation", { field: t("tnc") })),
    pp: yup
      .boolean()
      .oneOf([true], t("requiredValidation", { field: t("pp") })),
  });
  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      tnc: false,
      pp: false
    },
    resolver: yupResolver(schema),
  });

  const mutation = useMutation({
    mutationFn: (data) => {
      return createUser({
        name: data.name,
        email: data.email,
        password: data.password
      })
    },
    onSuccess: () => {
      setShowSuccess(true)
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error_message || t("registerError");
      showError(t("registerError"));
      setError("apiError", { message: errorMessage });
    }
  });

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          px: 5,
          py: 3,
          width: "400px",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          margin: "auto",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "26px",
            textTransform: "uppercase",
            mt: "15px",
          }}
        >
          {t("registration")}
        </Typography>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            marginTop: "20px",
          }}
          noValidate
          onSubmit={handleSubmit(mutation.mutate)}
        >
          <Controller
            render={({ field }) => (
              <TextField
                error={!!errors.name}
                fullWidth
                size="small"
                helperText={errors.name?.message}
                label={t("name")}
                margin="normal"
                name="name"
                type="name"
                variant="filled"
                {...field}
              />
            )}
            control={control}
            name="name"
          />
          <Controller
            render={({ field }) => (
              <TextField
                error={!!errors.email}
                fullWidth
                size="small"
                helperText={errors.email?.message}
                label={t("email")}
                margin="normal"
                name="email"
                type="email"
                variant="filled"
                {...field}
              />
            )}
            control={control}
            name="email"
          />
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                size="small"
                error={!!errors.password}
                helperText={errors.password?.message}
                label={t("password")}
                margin="normal"
                name="password"
                type="password"
                variant="filled"
                {...field}
              />
            )}
            control={control}
            name="password"
          />
          <Controller
            render={
              ({ field }) =>
                <FormControl
                  fullWidth
                  error={!!errors.tnc}
                  variant="standard"
                  sx={{ mt: 2 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox {...field}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                      />}
                    label=
                      {
                        <Box sx={{ fontSize: "14px" }}>
                          {t("tncFirst")} <Link href={import.meta.env.VITE_TNC_LINK}
                                                target={"_blank"}
                                                color={"inherit"}>{t("tncLink")}.</Link>
                        </Box>
                      }/>
                  {!!errors.tnc &&
                    <FormHelperText>{errors.tnc.message}</FormHelperText>}
                </FormControl>
            }
            control={control}
            name="tnc"
          />
          <Controller
            render={
              ({ field }) =>
                <FormControl
                  fullWidth
                  error={!!errors.pp}
                  variant="standard"
                  sx={{ mt: 2 }}
                >
                  <FormControlLabel disableTypography={true}
                                    control={
                                      <Checkbox {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}/>}
                                    label=
                                      {
                                        <Box sx={{ fontSize: "14px" }}>
                                          {t("ppFirst")} <Link href={import.meta.env.VITE_PP_LINK}
                                                               target={"_blank"}
                                        color={"inherit"}>{t("ppLink")}.</Link>
                                        </Box>
                                      }/>
                  {!!errors.pp &&
                    <FormHelperText>{errors.pp.message}</FormHelperText>}
                </FormControl>
            }
            control={control}
            name="pp"
          />
          {errors.apiError && (
            <Box sx={{ mt: 1 }}>
              <FormHelperText error>{errors.apiError.message}</FormHelperText>
            </Box>
          )}
          <Button variant="contained" type="submit" sx={{ mt: 2 }}>
            {t("register")}
          </Button>
          <OkDialog
            open={showSuccess}
            onClose={handleCloseSuccessDialog}
            text={t("registerSuccess")}
            buttonText={t("ok")}
          />
        </Box>
      </Card>
    </Container>
  );
}
